const imageBase = 'https://activity-topic.invalid';
const compare = (left, right) => left < right ? -1 : left > right ? 1 : 0;
const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const text = value => typeof value === 'string' ? value.trim() : '';

function activityIdString(value) {
  if (typeof value === 'number') return Number.isSafeInteger(value) && value > 0 ? String(value) : '';
  // Keep int64 string IDs intact: coercing them to Number can change the target.
  return typeof value === 'string' && /^[1-9]\d*$/.test(value.trim()) ? value.trim() : '';
}

/** A uni.navigateTo URL; an invalid activity ID has no navigation target. */
export function topicActivityUrl(activityId, { dark = false } = {}) {
  const id = activityIdString(activityId);
  return id ? `/pages/activity-detail/index?id=${encodeURIComponent(id)}${dark ? '&isDark=1' : ''}` : '';
}

function coverUrl(value) {
  const source = text(value);
  if (!source || /[\s\u0000-\u001f\u007f\\]/.test(source) || /%(?:0[0-9a-f]|1[0-9a-f]|7f|5c)/i.test(source)) return '';
  const local = /^\/(?!\/)/.test(source);
  if (!local && !/^https?:\/\/[^/?#]/i.test(source)) return '';
  try {
    const url = new URL(source, imageBase);
    if (!['http:', 'https:'].includes(url.protocol) || !url.hostname || url.username || url.password) return '';
    if (local && url.origin !== imageBase) return '';
    // These bundled preview images are not production campaign artwork.
    if (/^\/static\/images\/activity-demo-/i.test(decodeURIComponent(url.pathname))) return '';
    return source;
  } catch (_) {
    return '';
  }
}

function orderNumber(value) {
  if (typeof value !== 'number' && !(typeof value === 'string' && /^\d+$/.test(value.trim()))) return null;
  const number = Number(value);
  return Number.isSafeInteger(number) && number >= 0 ? number : null;
}

function stageOrder(value) {
  const number = orderNumber(value);
  if (number !== null) return [0, number];
  const name = text(value);
  // No preview stage names or guessed enum sequence: use the configured value.
  return name ? [1, name] : [2, ''];
}

function comparePlacements(left, right) {
  return compare(left.stage[0], right.stage[0]) || compare(left.stage[1], right.stage[1]) ||
    compare(left.position, right.position) || compare(left.index, right.index);
}

function periodDate(value) {
  const number = orderNumber(value);
  if (number === null || number === 0) return null;
  const timestamp = number < 1e12 ? number * 1000 : number;
  // API epochs are displayed in the site's UTC+8 calendar, independent of host TZ.
  const date = new Date(timestamp + 8 * 3600000);
  if (!Number.isFinite(date.getTime()) || date.getUTCFullYear() > 9999) return null;
  return { timestamp, label: date.toISOString().slice(0, 10) };
}

function topicPeriod(topic) {
  const start = periodDate(topic.startAt);
  const end = periodDate(topic.endAt);
  if (start && end) return start.timestamp <= end.timestamp ? `${start.label}至${end.label}` : '';
  if (start) return `${start.label}起`;
  if (end) return `截至${end.label}`;
  return '';
}

/**
 * Accept the unwrapped result from activityApi.worldCupTopic().
 * Return only the existing ActivityTopic model: no inferred hero, demo artwork,
 * stage UI or activity-type aliases. item.kind is the real ID emitted on click;
 * item.id identifies a placement, including repeated placements of one activity.
 */
export function adaptActivityTopic(response, { dark = false } = {}) {
  if (!record(response)) return { title: '', hero: null, items: [] };
  const title = text(response.title) || text(response.mainTitle);
  const period = topicPeriod(response);
  const placements = [];
  if (Array.isArray(response.activities)) response.activities.forEach((activity, index) => {
    if (!record(activity)) return;
    const kind = activityIdString(activity.activityId);
    if (!kind) return;
    const itemTitle = text(activity.title);
    const cover = coverUrl(activity.coverUrl);
    if (!itemTitle && !cover) return;
    placements.push({
      stage: stageOrder(activity.stageType),
      position: orderNumber(activity.positionNo) ?? Infinity,
      index,
      item: {
        id: `topic-${kind}-${index}`,
        kind,
        href: `#${topicActivityUrl(kind, { dark })}`,
        title: itemTitle,
        cover,
        period
      }
    });
  });
  return { title, hero: null, items: placements.sort(comparePlacements).map(placement => placement.item) };
}
